/**
 * Compatibility check for cross-placement Targeted reuse.
 *
 * Originally the picker filtered Targeted images to an exact `placement_key`
 * match. That ruled out a perfectly-good 1:1 crop made for one placement
 * being reused for another 1:1 placement (within the same platform, or even
 * across platforms — Meta 1:1 ↔ Google 1:1).
 *
 * The authoritative compatibility filter lives on the backend
 * (`MediaCRUD.get_images`'s `compatible_with_placement_key` arg) — that's
 * where the upscale cap and the spec registry actually live. This module is
 * a *defensive client-side backstop* applied post-fetch, intended to catch
 * registry drift between backend `SPEC_REGISTRY` and the frontend's
 * `usePlatformSpecs` payload (or any future bug in the SQL filter).
 *
 * It only checks attributes that are static spec metadata available to the
 * frontend:
 *
 *  - **Aspect ratio**: a Targeted's bytes are crop-locked to its origin
 *    spec's `aspect_ratio_float`, so `width/height` of the row is essentially
 *    that ratio (within `smart_crop_image`'s 0.02 tolerance). We compare
 *    `width/height` to the active spec's ratio with the same tolerance.
 *
 *  - **Filesize**: the new bytes will be re-encoded for the active placement.
 *    Same parent crop, same JPEG quality, similar dims → similar file size.
 *    Filtering on the existing Targeted's `file_size_bytes` against the
 *    active placement's cap is a heuristic that correctly rejects the
 *    common cross-platform mismatch (Meta 30 MB cap → Google 5 MB cap).
 *
 * Pixel-adequacy is *not* checked here — it depends on
 * `IMAGE_RESIZE_MAX_UPSCALE_FACTOR`, which is configurable via env var on
 * the backend. Hardcoding the cap on the client risks a stricter-than-server
 * filter if ops bumps the cap; the server's compatibility filter already
 * enforces this correctly using the live setting. If you need a client-side
 * pixel-adequacy check (e.g. validation in a future flow that doesn't
 * round-trip), source the cap from the backend first instead of hardcoding.
 */
import type { CropBox, MediaImage } from '@/types/media';

export interface TargetedFilterCriteria {
  /** Active placement spec's `aspect_ratio_float`. */
  aspectRatio: number;
  /** Active placement spec's `max_filesize_bytes`. */
  maxFilesizeBytes: number;
  /**
   * Aspect-ratio match tolerance. Defaults to 0.02 to match
   * `smart_crop_image` — Targeteds are crop-locked at creation to within
   * this tolerance, so a stricter check would needlessly reject valid
   * candidates.
   */
  aspectRatioTolerance?: number;
}

/**
 * Returns true iff `image` is a Targeted compatible with the active
 * placement under `criteria`. Returns false for non-Targeted rows so the
 * caller can use this as a single predicate over a mixed listing.
 */
export function isTargetedCompatible(
  image: MediaImage,
  criteria: TargetedFilterCriteria,
): boolean {
  if (image.category !== 'targeted') return false;
  // Legacy rows pre-`crop_box` column can't be reused — the materialize
  // path needs the box to send to the backend.
  if (!image.crop_box) return false;

  const tolerance = criteria.aspectRatioTolerance ?? 0.02;

  // Aspect ratio: w/h vs active spec.
  if (image.height <= 0 || image.width <= 0) return false;
  const imgRatio = image.width / image.height;
  if (Math.abs(imgRatio - criteria.aspectRatio) > tolerance) return false;

  // Filesize heuristic: the existing Targeted's bytes are a good proxy for
  // the new bytes' size (same parent crop, same encode quality, similar
  // dims). If the existing already exceeds the active cap, the new will too.
  if (image.file_size_bytes > criteria.maxFilesizeBytes) return false;

  return true;
}

/**
 * Convenience constructor for `TargetedFilterCriteria` from a placement
 * spec record. The spec shape mirrors `PlatformPlacementSpec` from the
 * `usePlatformSpecs` hook.
 */
export function criteriaFromSpec(spec: {
  aspect_ratio_float: number;
  max_filesize_bytes: number;
}): TargetedFilterCriteria {
  return {
    aspectRatio: spec.aspect_ratio_float,
    maxFilesizeBytes: spec.max_filesize_bytes,
  };
}

// Re-export CropBox so consumers don't need to chase imports through media.ts.
export type { CropBox };
